/**
 * Intenção de refeições detetada no TEXTO do pedido atual.
 *
 * Módulo puro e multilingue (pt/en/es/fr/it/de): sem IA, sem Maps, sem custos.
 * Devolve `null` quando o texto não fala de refeições — nesse caso vale a
 * configuração predefinida, nunca a preferência de uma pesquisa anterior.
 */
import type { MealPreference } from "./meal-preference";
import { isCategoryExcluded, parseNegativePreferences } from "./negative-preferences";

const fold = (value: string) =>
  (value ?? "").normalize("NFD").replace(/[\u0300-\u036f]/gu, "").toLowerCase();

/** "só uma pausa", "break without a restaurant", … */
const FREE_BREAK_PATTERNS: RegExp[] = [
  /\b(?:apenas|so|somente)\s+(?:uma\s+)?pausa\b/u,
  /\bpausa\s+(?:livre|neutra)\b/u,
  /\bpausa\s+(?:sem|s\/)\s+restaurante/u,
  /\b(?:just|only)\s+a\s+break\b/u,
  /\bfree\s+break\b/u,
  /\bbreak\s+without\s+(?:a\s+)?restaurant/u,
  /\b(?:solo|solamente)\s+(?:una\s+)?(?:pausa|parada)\b/u,
  /\b(?:juste|seulement)\s+une\s+pause\b/u,
  /\bpausa\s+libera\b/u,
  /\bnur\s+eine\s+pause\b/u,
];

/** "quero sugestões de restaurantes", "include restaurants", … */
const RESTAURANT_PATTERNS: RegExp[] = [
  /\b(?:quero|queremos|gostava\s+de|gostaria\s+de|pretendo|da[- ]?me|inclui\w*|incluir|sugere\w*|sugerir|recomenda\w*|com)\b[^.;!?\n]{0,40}\b(?:restaurantes?|almoc\w+|jantar\w*|refei\w+|gastronomia|onde\s+comer)\b/u,
  /\bsugest\w+\s+(?:de\s+)?(?:restaurantes?|gastronomi\w+|comida)\b/u,
  /\bonde\s+(?:comer|almocar|jantar)\b/u,
  // Menção direta e contável a restaurante(s) no pedido ("um restaurante japonês",
  // "a restaurant", "2 restaurantes") — pedido explícito, mesmo sem verbo.
  /\b(?:um|uma|dois|duas|a|an|one|two|\d+)\s+(?:bom\s+|boa\s+|good\s+)?restaurantes?\b/u,
  /\b(?:want|would\s+like|include|suggest|recommend|with)\b[^.;!?\n]{0,40}\b(?:restaurants?|lunch|dinner|meals?|where\s+to\s+eat|food\s+suggestions?)\b/u,
  /\b(?:quiero|queremos|incluir|sugerir|recomendar|con)\b[^.;!?\n]{0,40}\b(?:restaurantes?|almuerzo|cena|comida)\b/u,
  /\b(?:veux|voulons|inclure|sugg\w+|recommand\w+|avec)\b[^.;!?\n]{0,40}\b(?:restaurants?|dejeuner|diner|repas)\b/u,
  /\b(?:voglio|vogliamo|includi\w*|suggeris\w*|con)\b[^.;!?\n]{0,40}\b(?:ristorant\w+|pranzo|cena|past\w+)\b/u,
  /\b(?:m[oö]chte|will|einschliess\w*|empfehl\w*|mit)\b[^.;!?\n]{0,40}\b(?:restaurants?|mittagessen|abendessen|essen)\b/u,
];

/** "sem refeições", "no meals", … (independente da categoria restaurante) */
const NO_MEALS_PATTERNS: RegExp[] = [
  /\bsem\s+(?:refei\w+|almoc\w+|jantar\w*|paragens?\s+para\s+comer)\b/u,
  /\bnao\s+(?:quero|queremos|preciso|precisamos|incluir?)\b[^.;!?\n]{0,40}\b(?:refei\w+|almoc\w+|jantar\w*)\b/u,
  /\b(?:no|without)\s+(?:meals?|lunch|dinner|food\s+stops?)\b/u,
  /\bsin\s+(?:comidas?|almuerzo|cena)\b/u,
  /\bsans\s+(?:repas|dejeuner|diner)\b/u,
  /\bsenza\s+(?:past\w+|pranzo|cena)\b/u,
  /\bkeine\s+(?:mahlzeiten|essenspausen)\b/u,
];

/**
 * Intenção explícita do texto atual, ou `null` se o texto nada disser.
 * Precedência interna: pausa livre → negação → pedido de restaurantes.
 */
export function detectMealIntent(text: string): MealPreference | null {
  const value = fold(text);
  if (!value.trim()) return null;
  if (FREE_BREAK_PATTERNS.some((re) => re.test(value))) return "free_break";
  if (NO_MEALS_PATTERNS.some((re) => re.test(value))) return "none";
  // "Sem restaurantes" ≠ "sem refeições": a pessoa recusou restaurantes
  // como paragens, não as pausas — a pausa torna-se livre (free_break),
  // nunca "none" (que removeria os cartões de almoço/jantar).
  if (isCategoryExcluded(parseNegativePreferences(text), "restaurant")) return "free_break";
  if (RESTAURANT_PATTERNS.some((re) => re.test(value))) return "restaurant";
  return null;
}
